import type { TOC } from '@ember/component/template-only';
import { concat } from '@ember/helper';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import type Review from 'lumen/models/reviews/review';
import type { TranslationRequest } from 'lumen/types/review-manager';

interface HotelReviewsReviewDetailContentSignature {
  Args: {
    isTranslating: boolean;
    onRevert: () => void;
    onTranslate: () => Promise<void>;
    review: Review;
    showTranslateButtons: boolean;
    showTranslatedVersion: boolean;
    translations: TranslationRequest;
  };
}

<template>
  <section>
    <div class="flex flex-col gap-400">
      {{#if @review.content}}
        <p
          class="paragraph-300--regular"
          data-test-id={{concat "review-text-content-" @review.id}}
        >
          {{#if (and @showTranslatedVersion @translations.content)}}
            {{@translations.content}}
          {{else}}
            {{@review.content}}
          {{/if}}
        </p>
      {{/if}}

      {{#if @review.positive}}
        <div class="flex gap-200">
          <PrismIcon @name="feedback-positive-outline" @size="medium" />
          <p
            class="paragraph-300--regular"
            data-test-id={{concat "review-text-positive-" @review.id}}
          >
            {{#if (and @showTranslatedVersion @translations.positive)}}
              {{@translations.positive}}
            {{else}}
              {{@review.positive}}
            {{/if}}
          </p>
        </div>
      {{/if}}

      {{#if @review.negative}}
        <div class="flex gap-200">
          <PrismIcon @name="feedback-negative-outline" @size="medium" />
          <p
            class="paragraph-300--regular"
            data-test-id={{concat "review-text-negative-" @review.id}}
          >
            {{#if (and @showTranslatedVersion @translations.negative)}}
              {{@translations.negative}}
            {{else}}
              {{@review.negative}}
            {{/if}}
          </p>
        </div>
      {{/if}}
    </div>
  </section>
  {{#if @showTranslateButtons}}
    <section class="mt-400">
      {{#if @showTranslatedVersion}}
        <div class="flex items-center gap-200">
          <PrismButton
            @buttonType="secondary"
            @iconBefore="translate"
            @label={{t "review_manager.button.revert_translation.label"}}
            @onClick={{@onRevert}}
            data-test-id="revert-translation-button"
          />
          <p class="text-200--regular">{{t
              "review_manager.translated_by_google"
            }}</p>
        </div>
      {{else}}
        <PrismButton
          @buttonType="secondary"
          @iconBefore="translate"
          @isDisabled={{@isTranslating}}
          @label={{t "review_manager.button.translate.label"}}
          @onClick={{@onTranslate}}
          data-test-id="translation-button"
        />
      {{/if}}
    </section>
  {{/if}}
</template> satisfies TOC<HotelReviewsReviewDetailContentSignature>;
